import { useEffect, useMemo, useRef, useState } from 'react';
import { TFunction, useTranslation } from 'react-i18next';
import { Form, Input, Button, Layout, DatePicker, Select } from 'antd';
import { useDispatch, useSelector } from 'react-redux';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { useForm } from 'react-hook-form';
import { useAuth0 } from '@auth0/auth0-react';
import { CheckCircleFilled } from '@ant-design/icons';
import { useHistory } from 'react-router-dom';
import { CountryDto } from 'api';
import FormItem from 'components/form/FormItem';
import { AppDispatch } from 'redux/store';
import OmniNotification from 'components/OmniNotification';
import {
	getUserInfo,
	updateUserAccountDetails,
} from 'redux/reducers/authenticationSlice';
import { getCountries } from 'redux/reducers/regionSlice';
import landing from 'assets/img/landing.svg';
import styles from './InitialSetupPage.module.less';
import moment from 'moment';
import { regionSelector } from 'redux/selectors/regionSelector';
import { userSelector } from 'redux/selectors/userSelector';
import RouteLeavingGuard from 'components/RouteLeavingGuard';
import { confirmResultType } from 'components/RouteLeavingGuard/RouteLeavingGuard';
interface FormValues {
	firstName: string;
	lastName: string;
	username: string;
	dateOfBirth: string;
	country: string;
}
const { Option } = Select;
const getValidationSchema = (t: TFunction) =>
	yup.object().shape({
		firstName: yup
			.string()
			.required(
				t('validation.required', {
					fieldName: t('InitialSetupPage.firstName'),
				}),
			)
			.max(
				50,
				t('validation.maxLength', {
					fieldName: t('InitialSetupPage.firstName'),
					count: 50,
				}),
			),
		lastName: yup
			.string()
			.required(
				t('validation.required', {
					fieldName: t('InitialSetupPage.lastName'),
				}),
			)
			.max(
				50,
				t('validation.maxLength', {
					fieldName: t('InitialSetupPage.lastName'),
					count: 50,
				}),
			),
		country: yup
			.string()
			.nullable()
			.required(
				t('validation.required', {
					fieldName: t('InitialSetupPage.Country'),
				}),
			),
		dateOfBirth: yup
			.string()
			.required(
				t('validation.required', {
					fieldName: t('InitialSetupPage.DateOfBirth'),
				}),
			)
			.nullable(),
		username: yup
			.string()
			.required(
				t('validation.required', {
					fieldName: t('InitialSetupPage.Username'),
				}),
			)
			.min(
				2,
				t('validation.minLength', {
					fieldName: t('InitialSetupPage.Username'),
					count: 2,
				}),
			)
			.matches(/^[a-zA-Z0-9_—-]*$/, t('InitialSetupPage.CharacterRequirement'))
			.test(
				'matches',
				t('InitialSetupPage.StartEndRequirement'),
				(value: any) => {
					if (value.length === 1) {
						return /[A-Za-z0-9]/.test(value);
					} else {
						return /^[A-Za-z0-9]+.*[A-Za-z0-9]+$/.test(value);
					}
				},
			)
			.test(
				'matches',
				t('InitialSetupPage.ConsecutiveDashes'),
				(value: any) => /(-|_){2,}|(—){1,}/g.test(value) === false,
			)
			.max(
				15,
				t('validation.maxLength', {
					fieldName: t('InitialSetupPage.Username'),
					count: 15,
				}),
			)
			.strict(),
	});

const RedirectUrl = '/projects';

export default function InitialSetupPage() {
	const { t } = useTranslation();
	const { user, getAccessTokenSilently } = useAuth0();
	const dispatch: AppDispatch = useDispatch();
	const userData = useSelector(userSelector);
	const history = useHistory();
	const validationSchema = useMemo(() => getValidationSchema(t), [t]);
	const [countryOptions, setCountryOptions] = useState<any>();
	const countries = useSelector(regionSelector);
	const [invalidCharColor, setInvalidCharColor] = useState('grey');
	const [startEndColor, setStartEndColor] = useState('grey');
	const [consecutiveDashesColor, setConsecutiveDashesColor] = useState('grey');
	const [lengthColor, setLengthColor] = useState('grey');
	const validColor = '#82DC36';
	const invalidColor = 'red';
	const { control, handleSubmit, formState, reset } = useForm<FormValues>({
		mode: 'all',
		criteriaMode: 'all',
		resolver: yupResolver(validationSchema),
		defaultValues: {
			firstName: '',
			lastName: '',
			username: '',
			dateOfBirth: '',
			country: '',
		},
	});
	const { isSubmitting, errors, isDirty } = formState;

	const [
		shouldCallSubmitWhenNavigatingAway,
		setShouldCallSubmitWhenNavigatingAway,
	] = useState(false);
	const [confirmResult, setConfirmResult] = useState<confirmResultType>({
		loading: false,
		result: false,
	});
	const manuallySave = useRef(false);

	useEffect(() => {
		if (shouldCallSubmitWhenNavigatingAway) {
			manuallySave.current = false;
			handleSubmit(onSubmit)();
		}
	}, [shouldCallSubmitWhenNavigatingAway]);

	useEffect(() => {
		if (history.location.pathname === '/edit-profile') {
			if (userData !== null) {
				reset({
					firstName: userData.firstName,
					lastName: userData.lastName,
					username: userData.userName?.name,
					dateOfBirth: moment(
						moment(userData.metadata?.dateOfBirth).format('MM-DD-YYYY'),
						'MM-DD-YYYY',
					),
					country: userData.metadata?.location,
				});
				changesMade();
			}
		}
	}, [userData]);

	useEffect(() => {
		let countriesRetrieved = false;
		if (countries.length === 0) {
			dispatch(getCountries()).then((countryResults) => {
				if (
					getCountries.fulfilled.match(countryResults) &&
					!countriesRetrieved
				) {
					setCountryOptions(countryResults.payload);
				}
			});
		} else {
			setCountryOptions(countries);
		}

		return () => {
			countriesRetrieved = true;
		};
	}, [countryOptions, countries, dispatch]);

	const colorUsernameValidity = () => {
		let allErrors: any[] = [];
		const matches = errors.username?.types?.matches;
		if (matches) {
			if (Array.isArray(matches)) {
				allErrors = matches;
			} else if (typeof matches === 'string') {
				allErrors.push(matches);
			}
		}

		allErrors.push(errors.username?.types?.min ? 'min' : undefined);
		allErrors.push(errors.username?.types?.max ? 'max' : undefined);
		allErrors.push(errors.username?.types?.test);
		if (allErrors.includes(t('InitialSetupPage.CharacterRequirement'))) {
			setInvalidCharColor(invalidColor);
		} else {
			setInvalidCharColor(validColor);
		}
		if (allErrors.includes(t('InitialSetupPage.ConsecutiveDashes'))) {
			setConsecutiveDashesColor(invalidColor);
		} else {
			setConsecutiveDashesColor(validColor);
		}
		if (allErrors.includes('min') || allErrors.includes('max')) {
			setLengthColor(invalidColor);
		} else {
			setLengthColor(validColor);
		}
		if (allErrors.includes(t('InitialSetupPage.StartEndRequirement'))) {
			setStartEndColor(invalidColor);
		} else {
			setStartEndColor(validColor);
		}
	};
	const onSubmit = async (values: FormValues) => {
		if (!manuallySave.current) {
			setConfirmResult({
				loading: true,
				result: false,
			});
		}
		const { firstName, lastName, username, dateOfBirth, country } = values;
		const data = {
			firstName,
			lastName,
			username,
			dateOfBirth: moment(dateOfBirth).utc().format('YYYY-MM-DD'),
			country,
		};

		const result = await dispatch(updateUserAccountDetails(data));
		if (updateUserAccountDetails.fulfilled.match(result)) {
			await dispatch(getUserInfo());

			if (history.location.pathname === '/edit-profile') {
				history.goBack();
			} else {
				await getAccessTokenSilently();
				history.push(RedirectUrl);
			}

			OmniNotification.success({
				message: t('InitialSetupPage.form.Success.title'),
				description: t('InitialSetupPage.form.Success'),
			});

			if (!manuallySave.current) {
				setConfirmResult({
					loading: false,
					result: true,
				});
			}
		} else {
			if (!manuallySave.current) {
				setConfirmResult({
					loading: false,
					result: false,
				});
			}
		}
	};

	const changesMade = () => {
		colorUsernameValidity();
	};

	return (
		<>
			<RouteLeavingGuard
				when={isDirty}
				confirmResult={confirmResult!}
				confirmHandler={() => {
					manuallySave.current = false;
					setShouldCallSubmitWhenNavigatingAway(true);
				}}
				isFormValid={formState.isValid}
			/>
			<div className={styles.landing}>
				<div className={styles.logo}>
					<img src={landing} alt="logo" />
				</div>

				<Form
					data-testid="initial-setup-form"
					layout="vertical"
					onFinish={handleSubmit(onSubmit)}
					className={styles.setup}
					onKeyUpCapture={changesMade}
				>
					<FormItem
						name="firstName"
						label={t('InitialSetupPage.firstName')}
						control={control}
						render={({ field }) => (
							<Input
								data-testid="firstNameInput"
								className={styles.setupInput}
								size="large"
								{...field}
							/>
						)}
						className={styles.setupFormItem}
					/>
					<FormItem
						name="lastName"
						label={t('InitialSetupPage.lastName')}
						control={control}
						render={({ field }) => (
							<Input
								data-testid="lastNameInput"
								className={styles.setupInput}
								size="large"
								{...field}
							/>
						)}
						className={styles.setupFormItem}
					/>
					<FormItem
						name="username"
						label={t('InitialSetupPage.Username')}
						control={control}
						className={styles.usernameInput}
						render={({ field }) => (
							<Input
								autoComplete="off"
								className={styles.setupInput}
								size="large"
								data-testid="userNameInput"
								{...field}
							/>
						)}
					/>
					<Layout className={styles.setupUsernameValidations}>
						<div>
							<CheckCircleFilled style={{ color: lengthColor }} />
							{t('InitialSetupPage.UsernameLengthRequirement')}
						</div>
						<div>
							<CheckCircleFilled style={{ color: invalidCharColor }} />
							{t('InitialSetupPage.CharacterRequirement')}
						</div>
						<div>
							<CheckCircleFilled style={{ color: consecutiveDashesColor }} />
							{t('InitialSetupPage.ConsecutiveDashes')}
						</div>
						<div>
							<CheckCircleFilled style={{ color: startEndColor }} />
							{t('InitialSetupPage.StartEndRequirement')}
						</div>
					</Layout>
					<FormItem
						name="dateOfBirth"
						label={t('InitialSetupPage.DateOfBirth')}
						control={control}
						render={({ field }) => (
							<DatePicker
								disabledDate={(date) => moment(date).isAfter(moment.now())}
								format="MM-DD-YYYY"
								placeholder="MM-DD-YYYY"
								className={styles.setupInput}
								data-testid="dateOfBirthPicker"
								{...field}
							/>
						)}
					/>
					<FormItem
						name="country"
						label={t('InitialSetupPage.Country')}
						control={control}
						render={({ field }) => (
							<Select
								bordered={false}
								showArrow={false}
								data-testid="countrySelect"
								className={styles.setupInput}
								size="large"
								{...field}
							>
								{countryOptions?.map((d: CountryDto) => {
									return (
										<Option key={d.code} value={d.code || ''}>
											{d.name}
										</Option>
									);
								})}
							</Select>
						)}
					/>
					<Button
						data-testid="submit-btn"
						loading={isSubmitting}
						type="primary"
						htmlType="submit"
						size="large"
					>
						{history.location.pathname === '/edit-profile'
							? t('InitialSetupPage.Save')
							: t('InitialSetupPage.SaveAndContinue')}
					</Button>
				</Form>
			</div>
		</>
	);
}
